import * as React from 'react';
import { graphql, type PageProps, type HeadProps } from 'gatsby';

import { BlogPostPage as BlogPost } from '../../components/BlogPostPage/BlogPostPage';
import { PageLayout } from '../../layouts/PageLayout/PageLayout';
import { themeToVariables } from '../../utils/theme';
import { SEO } from '../../components/SEO/SEO';
import { getBlogPostTags } from '../../utils/blogPost';
import { sortByOrder } from '../../utils/array';

interface PageContext {
  socialCard: string | null;
  categories: string[];
  posts: string[];
  products: string[];
}

const BlogPostPage = ({
  data,
  location,
  pageContext,
}: PageProps<Queries.BlogPostPageQuery>) => {
  const context = pageContext as PageContext;

  if (!data || !data.snippet?.data || !data.blogPost) {
    return null;
  }

  const snippet = data.snippet?.data as CustomTypes.BlogPostSnippet;

  const {
    authors,
    cta,
    bgImage,
    bgImageOriginal,
    bgImageMobileOriginal,
    isBgFullSize,
    categories,
    skills,
    services,
    coverImage,
    coverVideo,
    coverVideoLoop,
    description,
    body,
    createdAt,
    theme,
    title,
    translations,
    opensourceProjects,
    products,
    clients,
    dateISO,
    bgColorOverflow,
    randomColorIndex,
    customCss,
  } = data.blogPost;

  const { colors: themeVariables, meta: themeMeta } = themeToVariables(
    theme,
    bgImage && bgColorOverflow,
    randomColorIndex || 0,
  );

  const sortedClients = [...(clients || [])].sort(sortByOrder);

  const tags = getBlogPostTags({
    categories,
    skills,
    services,
    slice: false,
  });

  const productsEntries = [...(products || [])].sort(sortByOrder);

  const sortedOpesourceProjects = opensourceProjects
    .filter(
      (project) =>
        !productsEntries.some((product) => product.slug === project.slug),
    )
    .sort(sortByOrder);

  const bgImages = {
    default: bgImage,
    original: {
      desktop: bgImageOriginal,
      mobile: bgImageMobileOriginal,
    },
  };

  return (
    <PageLayout
      themeVariables={themeVariables}
      themeMeta={themeMeta}
      title={`${title}${snippet.pageTitle}`}
      description={description}
      location={location}
      article
      dateISO={dateISO}
      authors={authors}
      tags={tags}
      cover={coverImage?.publicURL || bgImage?.publicURL}
      socialCard={context.socialCard}
      withBgImage={Boolean(bgImage)}
      page="post"
      isBgFullSize={isBgFullSize}
    >
      <BlogPost
        authors={authors}
        body={body}
        title={title}
        tags={tags}
        coverImage={coverImage}
        coverVideo={coverVideo}
        coverVideoLoop={coverVideoLoop}
        bgImages={bgImages}
        isBgFullSize={isBgFullSize}
        bgColorOverflow={bgColorOverflow}
        createdAt={createdAt}
        dateISO={dateISO}
        translations={translations}
        relatedPosts={data.allBlogPost.edges}
        whiteBg={themeMeta.whiteBg}
        opensourceProjects={sortedOpesourceProjects}
        products={productsEntries}
        clients={sortedClients}
        description={description}
        snippet={snippet as CustomTypes.BlogPostSnippet}
        events={data.allEvent.edges}
        customCss={customCss}
        cta={cta}
      />
    </PageLayout>
  );
};

export default BlogPostPage;

export const Head = ({
  data,
  location,
  pageContext,
}: HeadProps<Queries.BlogPostPageQuery>) => {
  const context = pageContext as PageContext;

  if (!data || !data.snippet?.data || !data.blogPost) {
    return null;
  }

  const snippet = data.snippet?.data as CustomTypes.BlogPostSnippet;
  const {
    description,
    title,
    dateISO,
    authors,
    categories,
    skills,
    services,
    coverImage,
    bgImage,
    coverVideo,
  } = data.blogPost;

  const tags = getBlogPostTags({
    categories,
    skills,
    services,
    slice: false,
  });

  return (
    <SEO
      title={`${title}${snippet.pageTitle}`}
      description={description}
      pathname={location.pathname}
      article
      dateISO={dateISO}
      authors={authors}
      tags={tags}
      cover={coverImage?.publicURL || bgImage?.publicURL}
      video={
        coverVideo &&
        coverVideo.find((video) => video?.source.name === 'cover.h264')?.source
          .publicURL
      }
      socialCard={context.socialCard}
    />
  );
};

export const query = graphql`
  fragment BlogPostPageEntry on BlogPost {
    authors {
      ...authorFields
    }
    body
    customCss
    categories {
      title
      slug
    }
    coverImage {
      ...coverImageData
    }
    coverVideo {
      source {
        publicURL
        name
      }
      codec
    }
    coverVideoLoop
    bgImage {
      ...bgImageData
    }
    bgImageMobileOriginal {
      ...bgImageOriginalData
    }
    bgImageOriginal: bgImage {
      ...bgImageOriginalData
    }
    isBgFullSize
    bgColorOverflow
    createdAt(formatString: "MMMM D, YYYY")
    dateISO: createdAt
    description
    theme
    title
    translations {
      language
      title
      url
    }
    cta {
      kind
      title
      description
      buttonText
      calComLink
    }
    skills {
      slug
      title
      shortTitle
    }
    services {
      slug
      title
      shortTitle
    }
    opensourceProjects {
      ...opensourceProjectFancyListFields
    }
    products {
      ...productFancyListFields
    }
    clients {
      ...clientFancyListFields
      order
    }
    posts {
      ...blogPostFancyListFields
    }
    randomColorIndex
  }

  query BlogPostPage(
    $slug: String!
    $categories: [String!]!
    $posts: [String!]
  ) {
    snippet(slug: { eq: "post" }) {
      id
      data
    }
    allBlogPost(
      sort: [{ featured: DESC }, { createdAt: DESC }]
      filter: {
        rawCategories: { in: $categories }
        slug: { ne: $slug, nin: $posts }
      }
      limit: 4
    ) {
      edges {
        node {
          ...blogPostFancyListFields
        }
      }
    }
    blogPost(slug: { eq: $slug }) {
      ...BlogPostPageEntry
    }
    allEvent(
      sort: { event: { date_start: DESC } }
      filter: { rawPosts: { eq: $slug } }
      limit: 10
    ) {
      edges {
        node {
          ...eventFancyListFields
        }
      }
    }
  }
`;
